import { useMemo, useEffect } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Box, Button, DateTimePicker, Typography } from '@gapartners/ui';
import { SelectField } from '@gapartners/ui/select';
import { Warning as WarningIcon } from '@mui/icons-material';

import type { MetricGrouping } from '@/types/agentEvents';
import {
  MetricIntervalPeriod,
  OCCUPANCY_GROUPINGS,
  SELECTABLE_INTERVAL_PERIODS,
} from '@/types/agentEvents';

import type { MetricsFilterParams } from '@/utils/metricsParams';
import {
  clampStartToMaxRange,
  getDefaultMetricsTimezone,
  getMaxRangeHours,
  validateDateRange,
} from '@/utils/metricsParams';

export interface MetricsFilterInputsProps {
  value: MetricsFilterParams;
  onChange: (params: MetricsFilterParams) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  groupingsOptions?: readonly MetricGrouping[];
  refreshLabelKey?: string;
}

export function MetricsFilterInputs({
  value,
  onChange,
  onRefresh,
  isRefreshing = false,
  groupingsOptions = OCCUPANCY_GROUPINGS,
  refreshLabelKey = 'components.dashboard.refresh',
}: MetricsFilterInputsProps) {
  const { t } = useTranslation();

  const maxRangeHours = getMaxRangeHours(value.intervalPeriod);

  const startValue = useMemo(() => {
    return value.startTime ? new Date(value.startTime) : null;
  }, [value.startTime]);

  const endValue = useMemo(() => {
    return value.endTime ? new Date(value.endTime) : null;
  }, [value.endTime]);

  // Validate date range against backend constraints
  const dateRangeValidation = useMemo(() => {
    if (!value.startTime || !value.endTime) {
      return { isValid: true };
    }
    return validateDateRange(
      value.startTime,
      value.endTime,
      value.intervalPeriod
    );
  }, [value.startTime, value.endTime, value.intervalPeriod]);

  useEffect(() => {
    const maxHours = getMaxRangeHours(value.intervalPeriod);

    if (!isFinite(maxHours)) return;

    const start = new Date(value.startTime).getTime();
    const end = new Date(value.endTime).getTime();
    const diff = end - start;
    const maxMs = maxHours * 60 * 60 * 1000;

    if (diff >= maxMs) {
      const newStart = new Date(end - maxMs + 60 * 1000);
      onChange({
        ...value,
        startTime: newStart.toISOString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.intervalPeriod]); // Only depend on interval, not startTime/endTime to avoid infinite loops

  const startMin = useMemo(() => {
    if (!endValue) return undefined;
    const min = new Date(endValue.getTime() - maxRangeHours * 60 * 60 * 1000);
    return min;
  }, [endValue, maxRangeHours]);

  const startMax = useMemo(() => {
    if (!endValue) return undefined;
    return new Date(endValue);
  }, [endValue]);

  const endMin = useMemo(() => {
    if (!startValue) return undefined;
    return new Date(startValue);
  }, [startValue]);

  const endMax = useMemo(() => {
    if (!startValue) return undefined;
    const max = new Date(startValue.getTime() + maxRangeHours * 60 * 60 * 1000);
    return max;
  }, [startValue, maxRangeHours]);

  const handleIntervalChange = (period: MetricIntervalPeriod) => {
    // Clamp both start and end to fit the new interval's max range
    let clampedStart = value.startTime;
    const clampedEnd = value.endTime;

    const maxHours = getMaxRangeHours(period);
    if (isFinite(maxHours)) {
      const start = new Date(value.startTime).getTime();
      const end = new Date(value.endTime).getTime();
      const diff = end - start;
      const maxMs = maxHours * 60 * 60 * 1000;

      // If range exceeds limit, adjust start forward
      if (diff >= maxMs) {
        const newStart = new Date(end - maxMs + 60 * 1000);
        clampedStart = newStart.toISOString();
      }
    }

    onChange({
      ...value,
      intervalPeriod: period,
      startTime: clampedStart,
      endTime: clampedEnd,
    });
  };

  const handleStartChange = (date: Date | null) => {
    if (!date) return;

    const clampedStart = clampStartToMaxRange(
      date.toISOString(),
      value.endTime,
      value.intervalPeriod
    );
    onChange({
      ...value,
      startTime: clampedStart,
    });
  };

  const handleEndChange = (date: Date | null) => {
    if (!date) return;

    const clampedStart = clampStartToMaxRange(
      value.startTime,
      date.toISOString(),
      value.intervalPeriod
    );
    onChange({
      ...value,
      startTime: clampedStart,
      endTime: date.toISOString(),
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 2,
      }}
    >
      <DateTimePicker
        label={t('components.dashboard.startDate')}
        value={startValue}
        minDate={startMin}
        maxDate={startMax}
        onChange={handleStartChange}
      />

      <DateTimePicker
        label={t('components.dashboard.endDate')}
        value={endValue}
        minDate={endMin}
        maxDate={endMax}
        onChange={handleEndChange}
      />

      <SelectField
        label={t('components.dashboard.groupBy')}
        fullWidth
        variant="outlined"
        value={value.groupings[0] ?? groupingsOptions[0]}
        options={groupingsOptions.map(g => ({
          value: g,
          label: t(`components.dashboard.grouping.${g}`),
        }))}
        onChange={(e: SelectChangeEvent<unknown>) =>
          onChange({
            ...value,
            groupings: [e.target.value as MetricGrouping],
          })
        }
      />

      <SelectField
        label={t('components.dashboard.interval')}
        fullWidth
        variant="outlined"
        value={value.intervalPeriod}
        options={SELECTABLE_INTERVAL_PERIODS.map(p => ({
          value: p,
          label: t(`components.dashboard.intervalPeriod.${p}`),
        }))}
        onChange={(e: SelectChangeEvent<unknown>) =>
          handleIntervalChange(e.target.value as MetricIntervalPeriod)
        }
      />
      <SelectField
        label={t('components.dashboard.timeZone')}
        fullWidth
        variant="outlined"
        value={value.timeZone}
        options={[
          {
            value: value.timeZone,
            label: value.timeZone || getDefaultMetricsTimezone(),
          },
        ]}
        onChange={(e: SelectChangeEvent<unknown>) =>
          onChange({
            ...value,
            timeZone: e.target.value as string,
          })
        }
      />

      {onRefresh && (
        <Button
          variant="outlined"
          color="primary"
          onClick={onRefresh}
          disabled={isRefreshing || !dateRangeValidation.isValid}
          loading={isRefreshing}
          title={
            !dateRangeValidation.isValid
              ? dateRangeValidation.message
              : undefined
          }
        >
          {isRefreshing ? t('common.loading') : t(refreshLabelKey)}
        </Button>
      )}

      {!dateRangeValidation.isValid && (
        <Box sx={{ width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'error.main',
              mt: 1,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <WarningIcon sx={{ fontSize: '1.25rem' }} />
            {dateRangeValidation.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
